import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import employeeActionsAPI from "../utils/changesAPIs";
import bulkTranslations, { fmt, translateBulkMessage } from "../Constant/EmployeeActions.bulk.i18n";

// Header text (EN or AR, any case, "*" ignored) → canonical row key.
// Mirrors TEMPLATE_HEADER_ALIASES on the backend plus a few common variants.
const HEADER_ALIASES = {
  "client code": "clientCode", clientcode: "clientCode", code: "clientCode", "employee code": "clientCode",
  "كود العميل": "clientCode", "كود الموظف": "clientCode", "الكود": "clientCode",
  "infinity code": "infinityCode", infinitycode: "infinityCode", "inf code": "infinityCode",
  "employee name": "employeeName", name: "employeeName", "اسم الموظف": "employeeName", "الاسم": "employeeName",
  "action type": "actionType", action: "actionType", type: "actionType", "نوع الإجراء": "actionType", "الإجراء": "actionType",
  reason: "reason", "السبب": "reason",
  "effective date": "effectiveDate", "تاريخ السريان": "effectiveDate",
  "new gross salary": "newGrossSalary", "gross salary": "newGrossSalary", gross: "newGrossSalary",
  "الراتب الإجمالي الجديد": "newGrossSalary", "الراتب الإجمالي": "newGrossSalary",
  "new net salary": "newNetSalary", "net salary": "newNetSalary", net: "newNetSalary",
  "صافي الراتب الجديد": "newNetSalary", "صافي الراتب": "newNetSalary",
  "new insurance salary": "newInsuranceSalary", "insurance salary": "newInsuranceSalary", insurance: "newInsuranceSalary",
  "الأجر التأميني الجديد": "newInsuranceSalary", "الأجر التأميني": "newInsuranceSalary",
  "adjustment type": "adjustmentType", "نوع التعديل": "adjustmentType",
  "last working day": "lastWorkingDay", lwd: "lastWorkingDay", "آخر يوم عمل": "lastWorkingDay",
  "termination type": "terminationType", "نوع إنهاء الخدمة": "terminationType",
  "termination reason": "reason", "سبب إنهاء الخدمة": "reason",
  details: "reason", "التفاصيل": "reason", notes: "reason", "ملاحظات": "reason",
  "resignation date": "resignationDate", "تاريخ تقديم الاستقالة": "resignationDate", "تاريخ الاستقالة": "resignationDate",
  "resignation reason": "resignationReason", "سبب الاستقالة": "resignationReason",
  "eligible for rehire": "eligibleForRehire", rehire: "eligibleForRehire", "مؤهل لإعادة التوظيف": "eligibleForRehire",
};

// One sheet per action type (template v2). Sheet name → action type.
const SHEET_TYPES = {
  "salary adjustments": "salary_change", "salary adjustment": "salary_change", salaries: "salary_change",
  "تعديلات الرواتب": "salary_change", "تعديل الراتب": "salary_change",
  resignations: "resignation", resignation: "resignation", "الاستقالات": "resignation", "استقالة": "resignation",
  terminations: "terminate", termination: "terminate", "إنهاء الخدمة": "terminate", "انهاء الخدمة": "terminate",
};
const IGNORED_SHEETS = new Set(["instructions", "التعليمات"]);
const normalizeHeader = (h) => String(h ?? "").replace(/\*/g, "").trim().toLowerCase().replace(/\s+/g, " ");
const POLL_MS = 1500;

const newBatchKey = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

/**
 * Parse the uploaded workbook into canonical rows. Pure; throws Error with a
 * translated message for structural problems (so nothing reaches the server).
 */
export async function parseWorkbookFile(file, bt, maxRows) {
  if (!/\.(xlsx|xls|csv)$/i.test(file.name)) throw new Error(bt.errType);
  let workbook;
  try {
    const buffer = await file.arrayBuffer();
    workbook = XLSX.read(new Uint8Array(buffer), { type: "array" });
  } catch {
    throw new Error(bt.errRead);
  }

  const rows = [];
  const problems = [];
  let actionSheets = 0;

  for (const sheetName of workbook.SheetNames) {
    const key = sheetName.trim().toLowerCase();
    if (IGNORED_SHEETS.has(key)) continue;
    // raw: numbers stay numbers and real Excel date cells arrive as serials —
    // the server parses both, so no locale guessing happens in the browser.
    const json = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { raw: true, defval: "" });
    const headerKeys = new Set(
      Object.keys(json[0] || {}).map((h) => HEADER_ALIASES[normalizeHeader(h)]).filter(Boolean),
    );
    const sheetType = SHEET_TYPES[key] || null;
    // v2 templates: the sheet decides the type. Old single-sheet files: an
    // Action Type column decides per row. Anything else is not ours.
    if (!sheetType && !headerKeys.has("actionType")) continue;
    actionSheets += 1;
    if (!json.length) continue;

    const missing = [];
    if (!headerKeys.has("clientCode") && !headerKeys.has("infinityCode")) missing.push("Client Code");
    if (sheetType === "salary_change" && !headerKeys.has("effectiveDate")) missing.push("Effective Date");
    if ((sheetType === "resignation" || sheetType === "terminate") && !headerKeys.has("lastWorkingDay")) missing.push("Last Working Day");
    if (sheetType === "terminate" && !headerKeys.has("terminationType")) missing.push("Termination Type");
    if (missing.length) {
      problems.push(fmt(bt.errSheetColumns, { sheet: sheetName, cols: missing.join(", ") }));
      continue;
    }

    for (const raw of json) {
      const record = { sheet: sheetName, rowNumber: (raw.__rowNum__ ?? 0) + 1 };
      if (sheetType) record.actionType = sheetType;
      let hasValue = false;
      for (const [header, value] of Object.entries(raw)) {
        const field = HEADER_ALIASES[normalizeHeader(header)];
        if (!field) continue;
        const v = typeof value === "number" ? value : String(value ?? "").trim();
        if (v !== "") hasValue = true;
        record[field] = v;
      }
      if (sheetType) record.actionType = sheetType; // a stray column can't override the sheet
      if (hasValue) rows.push({ rowId: rows.length + 1, ...record });
    }
  }

  if (problems.length) throw new Error(problems.join(" "));
  if (!actionSheets) throw new Error(bt.errNoSheets);
  if (!rows.length) throw new Error(bt.errEmpty);
  if (rows.length > maxRows) throw new Error(fmt(bt.errTooMany, { n: rows.length, max: maxRows }));
  return rows;
}

export default function useBulkEmployeeActions({ language, onCompleted }) {
  const bt = bulkTranslations[language] || bulkTranslations.en;

  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [step, setStep] = useState("choose"); // choose | upload | review | submit
  const [meta, setMeta] = useState(null);
  const [metaError, setMetaError] = useState("");
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [templateLang, setTemplateLang] = useState(language === "ar" ? "ar" : "en");
  const [templateBusy, setTemplateBusy] = useState(false);

  const [file, setFile] = useState(null); // { name, size }
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null); // parsing | validating | committing
  const [error, setError] = useState("");
  const [validation, setValidation] = useState(null);
  const [batch, setBatch] = useState(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const batchKeyRef = useRef(null);
  const completedRef = useRef(false);

  // ── meta (allowed types, limits) ────────────────────────────────────────
  const loadMeta = useCallback(async () => {
    setMetaError("");
    try {
      const m = await employeeActionsAPI.getBulkMeta();
      setMeta(m);
      // Selection is always a subset of what this admin level may do.
      setSelectedTypes((prev) => {
        const kept = prev.filter((x) => m.allowedTypes.includes(x));
        return kept.length ? kept : m.allowedTypes;
      });
    } catch (e) {
      setMetaError(e?.message || bt.genericError);
    }
  }, [bt.genericError]);

  const reset = useCallback(() => {
    setStep("choose");
    setFile(null);
    setRows([]);
    setBusy(null);
    setError("");
    setValidation(null);
    setBatch(null);
    setConfirmDiscard(false);
    batchKeyRef.current = null;
    completedRef.current = false;
  }, []);

  const openWizard = useCallback(() => {
    // Re-opening a minimized batch (running or just finished) brings its
    // progress / results back instead of starting over.
    if (batch && (batch.status === "processing" || minimized)) {
      setMinimized(false);
      setOpen(true);
      return;
    }
    if (batch) reset();
    setOpen(true);
    setMinimized(false);
    // Always refresh: the admin level's allowed action types may have changed.
    loadMeta();
  }, [batch, minimized, loadMeta, reset]);

  const closeWizard = useCallback((force = false) => {
    if (batch?.status === "processing") {
      // Never lose a running batch — closing minimizes it.
      setOpen(false);
      setMinimized(true);
      return;
    }
    if (!force && validation && !batch) {
      setConfirmDiscard(true);
      return;
    }
    setOpen(false);
    setMinimized(false);
    reset();
  }, [batch, validation, reset]);

  const minimize = useCallback(() => {
    setOpen(false);
    setMinimized(true);
  }, []);

  // ── step 1 ──────────────────────────────────────────────────────────────
  const toggleType = useCallback((type) => {
    if (!meta?.allowedTypes?.includes(type)) return;
    if (meta.allowedTypes.length === 1) return; // the only option stays selected
    setSelectedTypes((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));
  }, [meta]);

  const downloadTemplate = useCallback(async () => {
    if (!selectedTypes.length) return;
    setTemplateBusy(true);
    setError("");
    try {
      await employeeActionsAPI.downloadBulkTemplate(selectedTypes, templateLang);
    } catch (e) {
      setError(e?.message || bt.genericError);
    } finally {
      setTemplateBusy(false);
    }
  }, [selectedTypes, templateLang, bt.genericError]);

  // ── step 2 → 3 ──────────────────────────────────────────────────────────
  const validateRows = useCallback(async (parsedRows) => {
    setBusy("validating");
    setError("");
    try {
      const result = await employeeActionsAPI.validateBulkActions(parsedRows);
      setValidation(result);
      batchKeyRef.current = newBatchKey();
      setStep("review");
    } catch (e) {
      setError(e?.message || bt.genericError);
    } finally {
      setBusy(null);
    }
  }, [bt.genericError]);

  const handleFile = useCallback(async (f) => {
    if (!f) return;
    setError("");
    setValidation(null);
    setBusy("parsing");
    try {
      const parsed = await parseWorkbookFile(f, bt, meta?.maxRows || 1000);
      setFile({ name: f.name, size: f.size });
      setRows(parsed);
      setBusy(null);
      await validateRows(parsed);
    } catch (e) {
      setFile({ name: f.name, size: f.size });
      setRows([]);
      setError(e?.message || bt.errRead);
      setBusy(null);
    }
  }, [bt, meta, validateRows]);

  const removeFile = useCallback(() => {
    setFile(null);
    setRows([]);
    setValidation(null);
    setError("");
    setStep("upload");
  }, []);

  // ── step 3 → 4 ──────────────────────────────────────────────────────────
  const submittable = useMemo(
    () => (validation?.rows || []).filter((r) => r.status !== "error"),
    [validation],
  );

  const confirmSummary = useMemo(() => {
    const viaApproval = meta?.role === "admin_level";
    const now = viaApproval ? 0 : submittable.filter((r) => r.preview?.appliesNow).length;
    return {
      total: submittable.length,
      now,
      scheduled: viaApproval ? 0 : submittable.length - now,
      approval: viaApproval ? submittable.length : 0,
      skipped: (validation?.totals?.error) || 0,
    };
  }, [submittable, validation, meta]);

  const submit = useCallback(async () => {
    if (!submittable.length) return;
    setBusy("committing");
    setError("");
    try {
      const res = await employeeActionsAPI.commitBulkActions({
        rows,
        fileName: file?.name,
        clientBatchKey: batchKeyRef.current || (batchKeyRef.current = newBatchKey()),
      });
      const first = await employeeActionsAPI.getBulkBatch(res.batchId);
      setBatch(first);
      setStep("submit");
    } catch (e) {
      setError(e?.message || bt.genericError);
    } finally {
      setBusy(null);
    }
  }, [submittable, rows, file, bt.genericError]);

  // ── polling ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!batch || batch.status !== "processing") return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const next = await employeeActionsAPI.getBulkBatch(batch.id);
        if (!cancelled) setBatch(next);
      } catch {
        // transient — try again on the next tick
        if (!cancelled) setBatch((b) => ({ ...b }));
      }
    }, POLL_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [batch]);

  useEffect(() => {
    if (batch && batch.status !== "processing" && !completedRef.current) {
      completedRef.current = true;
      if (typeof onCompleted === "function") onCompleted(batch);
    }
  }, [batch, onCompleted]);

  const downloadReport = useCallback(async () => {
    if (!batch) return;
    try {
      await employeeActionsAPI.downloadBulkReport(batch.id, language === "ar" ? "ar" : "en");
    } catch (e) {
      setError(e?.message || bt.genericError);
    }
  }, [batch, language, bt.genericError]);

  /** Client-side error report of the review step (nothing submitted yet). */
  const downloadErrorReport = useCallback(() => {
    const issues = (validation?.rows || []).filter((r) => r.status !== "valid");
    const data = issues.map((r) => ({
      [bt.colSheet]: r.sheet || "",
      [bt.colRow]: r.rowNumber,
      [bt.colEmployee]: r.employee?.name || r.clientCode || "",
      "Client Code": r.clientCode || "",
      [bt.colAction]: r.actionType ? bt.types[r.actionType] : r.actionTypeRaw || "",
      [bt.colStatus]: r.status === "error" ? bt.statusError : bt.statusWarning,
      Details: r.messages.map((m) => translateBulkMessage(bt, m)).join(" · "),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = [{ wch: 20 }, { wch: 8 }, { wch: 28 }, { wch: 14 }, { wch: 20 }, { wch: 12 }, { wch: 90 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Issues");
    const base = (file?.name || "bulk-actions").replace(/\.(xlsx|xls|csv)$/i, "");
    XLSX.writeFile(wb, `${base}-issues.xlsx`);
  }, [validation, bt, file]);

  return {
    bt,
    // visibility
    open, minimized, openWizard, closeWizard, minimize,
    confirmDiscard, setConfirmDiscard,
    // state
    step, setStep, meta, metaError, loadMeta,
    selectedTypes, toggleType, templateLang, setTemplateLang, templateBusy, downloadTemplate,
    file, rows, busy, error, setError, validation, handleFile, removeFile, validateRows,
    submittable, confirmSummary, submit,
    batch, downloadReport, downloadErrorReport, reset,
  };
}
